import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { switchMap, timer } from 'rxjs';
import { LogoutAction } from '../../../state/action/logout.action';
import { TenapodExternalAuthAction } from '../../../state/action/tenapod-external-auth.action';

/**
 * Landing page reached when a partner is redirected from an external (Tenapod)
 * site with `email` and `path` query parameters in the URL. Shows a loading
 * message while the external auth call is in flight; on success the caller is
 * navigated to the target `path`, on failure an error is surfaced both inline
 * and via a PrimeNG toast.
 */
@UntilDestroy()
@Component({
  selector: 'partneradmin-tenapod-external-redirect-auth',
  templateUrl: './tenapod-external-redirect-auth.component.html',
  styleUrls: ['./tenapod-external-redirect-auth.component.scss'],
  providers: [MessageService],
})
export class TenapodExternalRedirectAuthComponent implements OnInit {
  private static readonly FALLBACK_PATH = '/dashboard';
  private static readonly FALLBACK_DELAY_MS = 3000;

  errorMessage: string | null = null;
  isRedirecting = false;

  #route: ActivatedRoute;
  #router: Router;
  #store: Store;
  #messageService: MessageService;
  #translocoService: TranslocoService;

  constructor(
    route: ActivatedRoute,
    router: Router,
    store: Store,
    messageService: MessageService,
    translocoService: TranslocoService
  ) {
    this.#route = route;
    this.#router = router;
    this.#store = store;
    this.#messageService = messageService;
    this.#translocoService = translocoService;
  }

  /**
   * Read `email`, `path` and `expiration_date` from the URL and start the
   * external auth flow. Missing parameters short-circuit with an inline +
   * toast error instead of hitting the backend.
   */
  ngOnInit(): void {
    const email = this.#route.snapshot.queryParamMap.get('email');
    const path = this.#route.snapshot.queryParamMap.get('path');
    const expirationDate = this.#route.snapshot.queryParamMap.get('expiration_date');

    if (!email || !path || !expirationDate) {
      this.showError(this.#translocoService.translate('USER.TENAPOD_EXTERNAL_AUTH.MISSING_PARAMS'));
      return;
    }

    this.runExternalAuth(email, path, expirationDate);
  }

  /**
   * Clear any existing session, then dispatch the external auth action.
   * Navigates to `path` on success; on failure surfaces the backend message
   * (or a generic fallback) inline and via toast.
   *
   * @param email          Partner email forwarded by the external site.
   * @param path           Target path to land on after a successful login.
   * @param expirationDate Titkosított lejárati időbélyeg az URL-ből, változatlanul
   *                       továbbítva a backend `expiration_date` body mezőjébe.
   */
  private runExternalAuth(email: string, path: string, expirationDate: string): void {
    this.#store
      .dispatch(new LogoutAction())
      .pipe(
        switchMap(() =>
          this.#store.dispatch(new TenapodExternalAuthAction(email, path, expirationDate))
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => this.navigateToPath(path),
        error: err => {
          const message =
            err?.error?.message ??
            this.#translocoService.translate('USER.TENAPOD_EXTERNAL_AUTH.GENERIC_ERROR');
          this.showError(message);
        },
      });
  }

  /**
   * Navigate to the requested target path. If the route cannot be matched —
   * the navigation Promise rejects or resolves to `false` — fall back to
   * `FALLBACK_PATH` and surface a toast so the user is informed that their
   * original target was unreachable.
   *
   * @param path Target path coming from the `path` query parameter.
   */
  private navigateToPath(path: string): void {
    this.#router
      .navigateByUrl(path)
      .then(success => {
        if (!success) {
          this.handleInvalidPath();
        }
      })
      .catch(() => this.handleInvalidPath());
  }

  /**
   * Surface an "invalid path" error inline and via toast, then redirect to
   * the fallback route after `FALLBACK_DELAY_MS`. The delay gives the user
   * time to actually read the message before being moved away from this
   * component (which owns the toast and is destroyed on navigation).
   */
  private handleInvalidPath(): void {
    this.isRedirecting = true;
    this.showError(this.#translocoService.translate('USER.TENAPOD_EXTERNAL_AUTH.INVALID_PATH'));
    timer(TenapodExternalRedirectAuthComponent.FALLBACK_DELAY_MS)
      .pipe(untilDestroyed(this))
      .subscribe(() =>
        this.#router.navigateByUrl(TenapodExternalRedirectAuthComponent.FALLBACK_PATH)
      );
  }

  /**
   * Display an error to the user. Replaces the loading state with an inline
   * error block and additionally shows a PrimeNG toast so it is visible on
   * mobile where the wrapper may be scrolled out of view.
   *
   * @param message Human-readable error message to display.
   */
  private showError(message: string): void {
    this.errorMessage = message;
    this.#messageService.add({
      key: 'tenapod-external-auth-error',
      severity: 'error',
      summary: this.#translocoService.translate('USER.TENAPOD_EXTERNAL_AUTH.ERROR_TITLE'),
      detail: message,
    });
  }
}
